/**
 * Registers a HUD element against a point on the studio plate's own pixels —
 * the ENG-069 hotspot pattern (see `.cc-station`), for elements that live
 * outside that box. `el` must already be a child of the aspect-locked stage,
 * which is `position: relative`, and the point is a percentage of that box.
 *
 * WHY PERCENTAGES AND NOT JS. This used to compute pixels from
 * `stage.clientWidth` and re-run on a `ResizeObserver`. `NO SIGNAL` painted at
 * the stage's top-left corner instead of on the glass, and the owner reported
 * it from a phone. Two faults in four lines, either enough on its own:
 *
 *   - `place()` ran at module scope, before `boot()` appended the stage. With
 *     no box yet, `clientWidth` and `clientHeight` are zero, so it wrote
 *     a left and a top of zero.
 *   - The observer that was supposed to correct that was constructed and
 *     dropped on the same line: `new ResizeObserver(place).observe(stage)`
 *     keeps no reference to the instance.
 *
 * HOW WRONG IT ACTUALLY WAS, because the first answer was too strong. Measured
 * on the deployed build, the inline `left` stayed at zero through load — but
 * watched for longer it does eventually snap onto the glass, on whatever resize
 * happens to come along, which on a phone is the URL bar collapsing on the
 * first scroll. So this was wrong from first paint until something nudged it,
 * rather than wrong forever. Harder to notice, and easier to talk yourself out
 * of, which is presumably how it shipped.
 *
 * A percentage on an absolutely positioned element already resolves against
 * the positioned ancestor's padding box — the same arithmetic, done by the
 * layout engine, with no timing to get wrong and nothing to keep alive.
 * `.cc-castle-hotspot` in `app.css` has always done it this way and has always
 * been correct, which is what made the comparison decisive: the transparent
 * button on the CRT glass was in the right place while the text on it was not.
 */

type Pct = readonly [number, number];

/** Anchors `el` centred on one point of the plate, e.g. text on the CRT screen. */
export function mountPointHotspot(el: HTMLElement, point: Pct): void {
  el.style.position = 'absolute';
  el.style.transform = 'translate(-50%, -50%)';
  el.style.left = `${point[0]}%`;
  el.style.top = `${point[1]}%`;
}
